package at.theatervogelweide.domain

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format.char
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.todayIn
import kotlin.time.Clock

// 31.12.2026, 31.12.26 or 31.12. (for the current year)
private val AUSTRIAN_DATE = Regex("""^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{2}|\d{4})?$""")
// 2026-12-31
private val ISO_DATE = Regex("""^(\d{4})-(\d{1,2})-(\d{1,2})$""")
// 19:30, 19.30, 19h30, 19 Uhr or 19:30 Uhr
private val AUSTRIAN_TIME = Regex("""^(\d{1,2})(?:[.h:](\d{2}))?(?:\s*uhr)?$""", RegexOption.IGNORE_CASE)

private val DATE_FORMAT = LocalDate.Format {
    day()
    char('.')
    monthNumber()
    char('.')
    year()
}

private val TIME_FORMAT = LocalTime.Format {
    hour()
    char(':')
    minute()
}

/**
 * All dates of the theater are entered and shown in its local time and written the Austrian way, e.g. `07.11.2026`
 * and `19:30`. The database stores timestamps in UTC without an offset.
 */
object TheaterTime {

    init {
        loadTimeZoneDatabase()
    }

    val timeZone: TimeZone = TimeZone.of("Europe/Vienna")

    /** Converts a timestamp as stored in the database into the theater's local time. */
    fun fromUtc(utc: LocalDateTime): LocalDateTime {
        return utc.toInstant(TimeZone.UTC).toLocalDateTime(timeZone)
    }

    /** Converts the theater's local time into a timestamp as stored in the database. */
    fun toUtc(theaterTime: LocalDateTime): LocalDateTime {
        return theaterTime.toInstant(timeZone).toLocalDateTime(TimeZone.UTC)
    }

    /** `07.11.2026` */
    fun format(date: LocalDate): String {
        return DATE_FORMAT.format(date)
    }

    /** `19:30` */
    fun format(time: LocalTime): String {
        return TIME_FORMAT.format(time)
    }

    /**
     * Parses `31.12.2026`, `31.12.26`, `31.12.` (in the year of [today]) and `2026-12-31`, `null` for anything else
     * and for dates which don't exist like `31.2.`.
     */
    fun parseDate(text: String, today: LocalDate = Clock.System.todayIn(timeZone)): LocalDate? {
        val trimmed = text.trim()
        AUSTRIAN_DATE.matchEntire(trimmed)?.let { match ->
            val (day, month, year) = match.destructured
            return localDateOrNull(year(year, today), month.toInt(), day.toInt())
        }
        ISO_DATE.matchEntire(trimmed)?.let { match ->
            val (year, month, day) = match.destructured
            return localDateOrNull(year.toInt(), month.toInt(), day.toInt())
        }
        return null
    }

    /** Parses `19:30`, `19.30`, `19h30`, `19 Uhr` and `19:30 Uhr`, `null` for anything else. */
    fun parseTime(text: String): LocalTime? {
        val match = AUSTRIAN_TIME.matchEntire(text.trim()) ?: return null
        val (hour, minute) = match.destructured
        return runCatching { LocalTime(hour.toInt(), minute.ifEmpty { "0" }.toInt()) }.getOrNull()
    }

    private fun year(value: String, today: LocalDate): Int {
        return when (value.length) {
            0 -> today.year
            2 -> 2000 + value.toInt()
            else -> value.toInt()
        }
    }

    private fun localDateOrNull(year: Int, month: Int, day: Int): LocalDate? {
        return runCatching { LocalDate(year, month, day) }.getOrNull()
    }
}
